import React, { useState, useEffect, Suspense } from 'react';
import Confetti from 'react-confetti';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Link,
  AppBar,
  Toolbar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Paper,
  Snackbar,
  Alert as MuiAlert,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Divider,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  Stack,
  Zoom,
} from '@mui/material';
import {
  Send as SendIcon,
  ContentCopy as ContentCopyIcon,
  YouTube as YouTubeIcon,
  Lightbulb as LightbulbIcon,
  PlayArrow as PlayArrowIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  Replay as ReplayIcon,
  Movie as MovieIcon,
  Language as LanguageIcon,
  CheckCircle as CheckCircleIcon,
  Search as SearchIcon,
  ArrowDownward as ArrowDownwardIcon,
  Settings as SettingsIcon,
  Star as StarIcon,
  HelpOutline as HelpOutlineIcon,
  ArrowUpward as ArrowUpwardIcon,
  PlaylistPlay as PlaylistPlayIcon,
  AspectRatio as AspectRatioIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence, useScroll } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Analytics } from '@vercel/analytics/react';
// import './App.css'; // App.css 내용은 MUI와 중복될 수 있어 대부분 제거/수정 필요

// Alert 컴포넌트 정의 (Snackbar와 함께 사용)
const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

// Framer Motion 애니메이션 Variants
const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.6 } },
};

const slideUp = {
  hidden: { y: 50, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { type: 'spring', stiffness: 100, damping: 15 } },
};

const slideRight = {
  hidden: { x: -50, opacity: 0 },
  visible: { x: 0, opacity: 1, transition: { type: 'spring', stiffness: 100, damping: 15 } },
};

const slideLeft = {
  hidden: { x: 50, opacity: 0 },
  visible: { x: 0, opacity: 1, transition: { type: 'spring', stiffness: 100, damping: 15 } },
};

const stagger = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

// 스크롤 인터랙션을 위한 애니메이션 컴포넌트
const AnimatedBox = ({ children, animation = fadeIn, threshold = 0.2, ...props }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold,
  });

  return (
    <motion.div ref={ref} initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={animation} {...props}>
      {children}
    </motion.div>
  );
};

const contentMaxWidth = '1200px'; // Define max width for content sections

function App() {
  const { t, i18n } = useTranslation();
  const [shortsUrlInput, setShortsUrlInput] = useState('');
  const [convertedUrl, setConvertedUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [appBarSolid, setAppBarSolid] = useState(false);
  const [videoInfo, setVideoInfo] = useState(null); // To store thumbnail and title
  const [isVideoInfoLoading, setIsVideoInfoLoading] = useState(false);

  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  });
  const [siteBaseUrl, setSiteBaseUrl] = useState('');

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info'); // 'success', 'error', 'warning', 'info'

  // 추가 인터랙션 효과를 위한 state
  const [showExampleUrl, setShowExampleUrl] = useState(false);
  const [totalConversions, setTotalConversions] = useState(() => {
    const savedConversions = localStorage.getItem('totalConversions');
    return savedConversions ? parseInt(savedConversions, 10) : Math.floor(Math.random() * 10000) + 5000;
  });
  const [isHovering, setIsHovering] = useState(false);

  // 스크롤 이펙트를 위한 refs
  const [featureRef, featureInView] = useInView({ threshold: 0.2, triggerOnce: true });
  const [howItWorksRef, howItWorksInView] = useInView({ threshold: 0.2, triggerOnce: true });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const { scrollYProgress } = useScroll();

  // Language change logic
  const getBasePath = () => {
    if (typeof window === 'undefined') return '/';
    const pathParts = window.location.pathname.split('/').filter((part) => part !== '' && !['en', 'ko', 'es'].includes(part));
    return '/' + pathParts.join('/');
  };

  const changeLanguage = (lng) => {
    if (typeof window === 'undefined') return;
    i18n.changeLanguage(lng);
    const basePath = getBasePath();
    // Ensure basePath starts with a single slash if it's not just "/"
    let currentPath = basePath;
    if (currentPath !== '/' && !currentPath.startsWith('/')) {
      currentPath = '/' + currentPath;
    }
    if (currentPath.endsWith('/') && currentPath.length > 1) {
      currentPath = currentPath.slice(0, -1);
    }

    let newPath;
    if (lng === 'en') {
      newPath = currentPath === '/' ? '/' : currentPath;
    } else {
      newPath = `/${lng}${currentPath === '/' ? '' : currentPath}`;
    }

    if (window.location.pathname !== newPath) {
      window.location.href = newPath;
    }
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setSiteBaseUrl(window.location.origin);

      // 스크롤 진행도 계산 및 AppBar 배경 변경
      const handleScroll = () => {
        if (window.scrollY > 50) {
          setAppBarSolid(true);
        } else {
          setAppBarSolid(false);
        }
      };

      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
      const detectedLanguage = i18n.language || 'en';
      document.documentElement.lang = detectedLanguage.split('-')[0];
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', handleResize);
      }
    };
  }, [i18n.language]);

  useEffect(() => {
    const interval = setInterval(() => {
      setTotalConversions((prev) => prev + 1);
    }, 30000); // Slower update for a more realistic feel
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    localStorage.setItem('totalConversions', totalConversions.toString());
  }, [totalConversions]);

  function convertShortsToVideo(url) {
    if (url.includes('youtube.com/shorts/')) {
      return url.replace('shorts/', 'watch?v=');
    } else if (url.includes('youtu.be/')) {
      const videoId = url.split('youtu.be/')[1]?.split('?')[0];
      if (videoId) {
        return `https://www.youtube.com/watch?v=${videoId}`;
      }
    }
    return null;
  }

  const showSnackbar = (message, severity = 'info') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const fetchVideoDetails = async (videoUrl) => {
    setIsVideoInfoLoading(true);
    setVideoInfo(null);
    try {
      // Using a try-catch for oEmbed as it might not always work or might have CORS issues
      // For a more robust solution, a backend proxy for oEmbed would be better to avoid CORS.
      const oembedUrl = `https://www.youtube.com/oembed?url=${encodeURIComponent(videoUrl)}&format=json`;
      const response = await fetch(oembedUrl);
      // Note: If YouTube oEmbed doesn't have permissive CORS headers, this fetch will fail in the browser.
      // If it fails, a serverless function acting as a proxy would be the way to go.
      if (!response.ok) {
        // throw new Error(`Failed to fetch video details: ${response.statusText}`);
        // Instead of throwing an error that stops everything, let's just log it and proceed without video info
        console.warn(
          `Could not fetch video details from oEmbed: ${response.status} ${response.statusText}. This might be a CORS issue or the video doesn't support oEmbed.`
        );
        setVideoInfo(null);
      } else {
        const data = await response.json();
        setVideoInfo({
          title: data.title,
          thumbnailUrl: data.thumbnail_url,
          authorName: data.author_name,
        });
      }
    } catch (error) {
      console.error('Error fetching video details:', error);
      setVideoInfo(null); // Clear info on error
    }
    setIsVideoInfoLoading(false);
  };

  const handleConvertClick = async () => {
    if (!shortsUrlInput.trim()) {
      showSnackbar(t('hintPasteError'), 'error');
      setConvertedUrl('');
      setVideoInfo(null);
      return;
    }
    setIsLoading(true);
    setConvertedUrl('');
    setShowConfetti(false);
    setVideoInfo(null);

    setTimeout(async () => {
      // Make the callback async
      const result = convertShortsToVideo(shortsUrlInput.trim());
      setIsLoading(false);
      if (result) {
        setConvertedUrl(result);
        showSnackbar(t('hintSuccess'), 'success');
        setShowConfetti(true);
        setTotalConversions((prev) => prev + 1);
        await fetchVideoDetails(result); // Fetch video details
        setTimeout(() => setShowConfetti(false), 5000);
      } else {
        setConvertedUrl('');
        setVideoInfo(null);
        showSnackbar(t('hintInvalidError'), 'error');
      }
    }, 1200);
  };

  // 예시 URL 표시/숨김 토글
  const toggleExampleUrl = () => {
    setShowExampleUrl(!showExampleUrl);
    if (!showExampleUrl && !shortsUrlInput) {
      setShortsUrlInput('https://youtube.com/shorts/example123');
    }
  };

  const handleCopyClick = () => {
    if (convertedUrl && navigator.clipboard) {
      navigator.clipboard
        .writeText(convertedUrl)
        .then(() => {
          showSnackbar(t('hintCopySuccess'), 'success');
        })
        .catch((err) => {
          console.error('Failed to copy: ', err);
          showSnackbar(t('hintCopyError'), 'error');
        });
    } else if (convertedUrl) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = convertedUrl;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        showSnackbar(t('hintCopyFallback'), 'success');
      } catch (err) {
        console.error('Fallback copy failed: ', err);
        showSnackbar(t('hintCopyError'), 'error');
      }
      document.body.removeChild(textArea);
    }
  };

  const handleInputChange = (e) => {
    setShortsUrlInput(e.target.value);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const scrollToConverter = () => {
    const element = document.getElementById('converter-section');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const currentLang = i18n.language || 'en';

  let pageSpecificPath = '';
  if (typeof window !== 'undefined') {
    const pathParts = window.location.pathname.split('/').filter((part) => part !== '' && !['en', 'ko', 'es'].includes(part));
    pageSpecificPath = '/' + pathParts.join('/');
    if (pageSpecificPath === '/' && pathParts.length > 0 && pathParts[0] !== '') {
      // 예: /about 인 경우 pageSpecificPath는 /about이 되어야 함.
    } else if (pageSpecificPath.length > 1 && pageSpecificPath.endsWith('/')) {
      pageSpecificPath = pageSpecificPath.slice(0, -1); // 마지막 / 제거
    }
  }
  if (pageSpecificPath === '//') pageSpecificPath = '/';

  const getCanonicalUrl = (lang) => {
    if (!siteBaseUrl) return '';
    let langSegment = '';
    if (lang !== 'en') {
      langSegment = '/' + lang;
    }
    let finalPath = pageSpecificPath;
    if (finalPath === '/') {
      finalPath = '';
    }
    return `${siteBaseUrl}${langSegment}${finalPath}`;
  };

  const canonicalUrl = getCanonicalUrl(currentLang.split('-')[0]);
  const alternateLangPaths = {
    en: `${siteBaseUrl}/`,
    ko: `${siteBaseUrl}/ko/`,
    es: `${siteBaseUrl}/es/`,
  };

  // SEO Content - simplified for now
  const pageTitle = t('pageTitle');
  const pageDescription = t('metaDescription');
  const ogImageUrl = `${siteBaseUrl}/thumbnail.jpg`; // Assuming thumbnail.jpg is in public

  // Example URLs per language
  const exampleUrls = {
    en: 'https://www.youtube.com/shorts/KrtYasOGibU',
    ko: 'https://www.youtube.com/shorts/KrtYasOGibU',
    es: 'https://www.youtube.com/shorts/KrtYasOGibU',
  };
  const currentExampleUrl = exampleUrls[currentLang.split('-')[0]] || exampleUrls.en;

  function AppLanguageSwitcher({ inAppBar = true }) {
    const { i18n } = useTranslation();
    const theme = useTheme();
    const currentLang = i18n.language || 'en';

    const handleLanguageChange = (event) => {
      const newLang = event.target.value;
      changeLanguage(newLang);
    };

    return (
      <FormControl size="small" variant="outlined" sx={{ minWidth: 120, m: 1 }}>
        <InputLabel id="language-select-label" sx={{ color: inAppBar && !appBarSolid && !isMobile ? 'white' : 'text.primary' }}>
          {t('language')}
        </InputLabel>
        <Select
          labelId="language-select-label"
          value={currentLang.split('-')[0]}
          onChange={handleLanguageChange}
          label={t('language')}
          sx={{
            color: inAppBar && !appBarSolid && !isMobile ? 'white' : 'text.primary',
            '.MuiOutlinedInput-notchedOutline': {
              borderColor: inAppBar && !appBarSolid && !isMobile ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.23)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: inAppBar && !appBarSolid && !isMobile ? 'white' : theme.palette.primary.main,
            },
            '.MuiSvgIcon-root': {
              color: inAppBar && !appBarSolid && !isMobile ? 'white' : 'text.primary',
            },
          }}
        >
          <MenuItem value="en">English</MenuItem>
          <MenuItem value="ko">한국어</MenuItem>
          <MenuItem value="es">Español</MenuItem>
        </Select>
      </FormControl>
    );
  }

  // Features data for dynamic rendering
  const features = [
    {
      icon: <PlayArrowIcon fontSize="large" color="primary" />,
      title: t('features.0.title'),
      description: t('features.0.description'),
    },
    {
      icon: <ContentCopyIcon fontSize="large" color="secondary" />,
      title: t('features.1.title'),
      description: t('features.1.description'),
    },
    {
      icon: <LanguageIcon fontSize="large" color="success" />,
      title: t('features.2.title'),
      description: t('features.2.description'),
    },
    {
      icon: <MovieIcon fontSize="large" color="error" />,
      title: t('features.3.title'),
      description: t('features.3.description'),
    },
  ];

  // How it works steps
  const howItWorksSteps = [
    { text: t('howItWorks.0.text'), icon: <SearchIcon /> },
    { text: t('howItWorks.1.text'), icon: <SendIcon /> },
    { text: t('howItWorks.2.text'), icon: <PlayArrowIcon /> },
    { text: t('howItWorks.3.text'), icon: <ContentCopyIcon /> },
  ];

  // Why Choose Us Data
  const whyChooseUsData = [
    {
      icon: <SettingsIcon fontSize="large" color="primary" />,
      title: t('whyChooseUs.0.title'),
      description: t('whyChooseUs.0.description'),
    },
    {
      icon: <PlaylistPlayIcon fontSize="large" color="secondary" />,
      title: t('whyChooseUs.1.title'),
      description: t('whyChooseUs.1.description'),
    },
    {
      icon: <AspectRatioIcon fontSize="large" color="success" />,
      title: t('whyChooseUs.2.title'),
      description: t('whyChooseUs.2.description'),
    },
    {
      icon: <CheckCircleIcon fontSize="large" color="info" />, // Re-using CheckCircleIcon
      title: t('whyChooseUs.3.title'),
      description: t('whyChooseUs.3.description'),
    },
  ];

  return (
    <Suspense fallback={<CircularProgress />}>
      <Helmet>
        <html lang={currentLang.split('-')[0]} />
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={canonicalUrl} />
        {Object.entries(alternateLangPaths).map(([lang, path]) => (
          <link key={lang} rel="alternate" hrefLang={lang} href={path} />
        ))}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:image:alt" content={t('ogImageAlt')} />
        <meta property="og:site_name" content={t('siteName')} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={ogImageUrl} />
      </Helmet>
      <Analytics />

      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', background: theme.palette.background.default }}>
        <AppBar
          position="fixed"
          elevation={appBarSolid ? 4 : 0}
          sx={{
            backgroundColor: appBarSolid ? theme.palette.background.paper : 'rgba(0,0,0,0.25)',
            backdropFilter: appBarSolid ? 'none' : 'blur(8px)',
            transition: 'background-color 0.3s ease-in-out, box-shadow 0.3s ease-in-out, backdrop-filter 0.3s ease-in-out',
          }}
        >
          <Toolbar sx={{ minHeight: { xs: 56, sm: 64, md: 72 } }}>
            <YouTubeIcon sx={{ mr: 1.5, fontSize: isMobile ? '2rem' : '2.5rem', color: appBarSolid || isMobile ? theme.palette.primary.main : 'white' }} />
            <Typography
              variant={isMobile ? 'h6' : 'h5'}
              component="div"
              sx={{ flexGrow: 1, fontWeight: 'bold', color: appBarSolid || isMobile ? 'text.primary' : 'white' }}
            >
              {t('siteName')}
            </Typography>
            <AppLanguageSwitcher inAppBar={true} />
          </Toolbar>
        </AppBar>

        {/* Spacer for fixed AppBar */}
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64, md: 72 } }} />

        {/* New Hero Section with Integrated Converter */}
        <Box
          id="converter-section"
          sx={{
            pt: isMobile ? 6 : 10,
            pb: isMobile ? 8 : 14,
            textAlign: 'center',
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            color: 'white',
            minHeight: isMobile ? 'calc(50vh - 72px)' : 'calc(60vh - 72px)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            px: 2,
          }}
        >
          <AnimatedBox animation={slideUp}>
            <Typography
              variant={isMobile ? 'h3' : 'h2'}
              component="h1"
              gutterBottom
              sx={{ fontWeight: 'bold', textShadow: '2px 2px 4px rgba(0,0,0,0.3)', mb: isMobile ? 1.5 : 2 }}
            >
              {t('heroTitle')}
            </Typography>
          </AnimatedBox>
          <AnimatedBox animation={fadeIn} sx={{ mb: isMobile ? 3 : 4 }}>
            <Typography variant={isMobile ? 'h6' : 'h5'} component="p" sx={{ maxWidth: '700px', margin: 'auto', opacity: 0.9 }}>
              {t('heroSubtitle')}
            </Typography>
          </AnimatedBox>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            style={{ width: isMobile ? '95%' : '80%', maxWidth: '750px' }}
          >
            <Paper
              elevation={10}
              sx={{
                p: isMobile ? 3 : 4,
                borderRadius: theme.shape.borderRadius * 0.75,
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.25)',
                boxShadow: theme.shadows[8],
              }}
            >
              <Grid container spacing={2} alignItems="stretch" justifyContent="center">
                <Grid item xs={12} md={9}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label={t('inputLabel')}
                    placeholder={t('inputPlaceholder')}
                    value={shortsUrlInput}
                    onChange={handleInputChange}
                    onKeyPress={(e) => e.key === 'Enter' && handleConvertClick()}
                    disabled={isLoading}
                    sx={{
                      height: '100%',
                      '& .MuiOutlinedInput-root': {
                        height: '100%',
                        background: 'rgba(0,0,0,0.1)',
                        '& fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                        '&:hover fieldset': { borderColor: 'white' },
                        '&.Mui-focused fieldset': { borderColor: 'white', borderWidth: '2px' },
                        color: 'white',
                      },
                      '& .MuiInputBase-input': {
                        color: 'white',
                        fontSize: isMobile ? '0.95rem' : '1rem',
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(255,255,255,0.9)',
                        fontWeight: 500,
                      },
                      '& .MuiInputLabel-root.Mui-focused': { color: 'white' },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="secondary"
                    size="large"
                    onClick={handleConvertClick}
                    disabled={isLoading}
                    startIcon={isLoading ? <CircularProgress size={24} color="inherit" /> : <SendIcon />}
                    sx={{
                      height: '100%',
                      fontWeight: 'bold',
                      fontSize: isMobile ? '0.9rem' : '1rem',
                      boxShadow: `0 6px 20px 0 ${theme.palette.secondary.dark}50`,
                      transition: 'transform 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                      '&:hover': {
                        transform: 'scale(1.03)',
                        boxShadow: `0 8px 25px 0 ${theme.palette.secondary.dark}70`,
                      },
                    }}
                  >
                    {isLoading ? t('converting') : t('convertButton')}
                  </Button>
                </Grid>
                <Grid item xs={12} sx={{ pt: 1.5 }}>
                  <Box sx={{ mt: 1, textAlign: 'center' }}>
                    <Button
                      variant="text"
                      onClick={toggleExampleUrl}
                      startIcon={<HelpOutlineIcon />}
                      sx={{
                        color: 'rgba(255,255,255,0.9)',
                        textTransform: 'none',
                        fontWeight: 500,
                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
                      }}
                    >
                      {showExampleUrl ? t('hideExample') : t('showExample')}
                    </Button>
                    {showExampleUrl && (
                      <Typography variant="caption" display="block" sx={{ color: 'rgba(255,255,255,0.8)', mt: 0.5, fontStyle: 'italic' }}>
                        {t('exampleUrlText')} {currentExampleUrl}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>

              {convertedUrl && (
                <AnimatedBox animation={fadeIn} sx={{ mt: 3, p: 2, background: 'rgba(0,0,0,0.15)', borderRadius: theme.shape.borderRadius * 0.5 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ color: 'white', fontWeight: 'bold' }}>
                    {t('convertedUrlLabel')}
                  </Typography>
                  <Link
                    href={convertedUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ display: 'block', wordBreak: 'break-all', color: theme.palette.secondary.light, textDecoration: 'underline', mb: 1 }}
                  >
                    {convertedUrl}
                  </Link>
                  <Button
                    variant="outlined"
                    color="inherit"
                    onClick={handleCopyClick}
                    startIcon={<ContentCopyIcon />}
                    sx={{
                      borderColor: 'rgba(255,255,255,0.7)',
                      color: 'white',
                      '&:hover': {
                        borderColor: 'white',
                        backgroundColor: 'rgba(255,255,255,0.1)',
                      },
                      mb: videoInfo || isVideoInfoLoading ? 2 : 0, // Add margin if video info is present or loading
                    }}
                  >
                    {t('copyButton')}
                  </Button>

                  {isVideoInfoLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', my: 2, textAlign: 'center' }}>
                      <CircularProgress size={30} color="inherit" />
                      <Typography variant="body2" sx={{ ml: 2, color: 'rgba(255,255,255,0.8)' }}>
                        Loading video preview...
                      </Typography>
                    </Box>
                  )}

                  {videoInfo && !isVideoInfoLoading && (
                    <AnimatedBox animation={fadeIn} sx={{ mt: 2, textAlign: 'center' }}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'white', mb: 1 }}>
                        {t('videoPreviewTitle', 'Video Preview:')}
                      </Typography>
                      <Grid container spacing={2} alignItems="center" justifyContent="center">
                        <Grid item xs={12} sm={6} md={5} sx={{ display: 'flex', justifyContent: 'center' }}>
                          <Box
                            component="img"
                            src={videoInfo.thumbnailUrl}
                            alt={t('videoThumbnailAlt', { title: videoInfo.title })}
                            sx={{
                              maxWidth: '100%',
                              width: '240px',
                              borderRadius: theme.shape.borderRadius * 0.5,
                              border: '1px solid rgba(255,255,255,0.3)',
                              aspectRatio: '16/9',
                              objectFit: 'cover',
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={7} sx={{ textAlign: isMobile ? 'center' : 'left' }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'white', lineHeight: 1.3 }}>
                            {videoInfo.title}
                          </Typography>
                          {videoInfo.authorName && (
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mt: 0.5 }}>
                              {t('videoAuthor', 'By:')} {videoInfo.authorName}
                            </Typography>
                          )}
                        </Grid>
                      </Grid>
                    </AnimatedBox>
                  )}
                </AnimatedBox>
              )}
            </Paper>
          </motion.div>

          {showConfetti && windowSize.width > 0 && windowSize.height > 0 && (
            <Confetti width={windowSize.width} height={windowSize.height} recycle={false} onConfettiComplete={() => setShowConfetti(false)} />
          )}
        </Box>

        {/* Features Section */}
        <Container maxWidth={contentMaxWidth} sx={{ py: isMobile ? 6 : 10, mx: 'auto' }} ref={featureRef}>
          <AnimatedBox animation={slideUp}>
            <Typography variant={isMobile ? 'h4' : 'h3'} component="h2" textAlign="center" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
              {t('featuresTitle')}
            </Typography>
            <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: isMobile ? 5 : 7, maxWidth: '800px', mx: 'auto', lineHeight: 1.7 }}>
              {t('featuresSubtitle')}
            </Typography>
          </AnimatedBox>
          <Grid container spacing={isMobile ? 3 : 3.5} justifyContent="center">
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={6} key={index}>
                <AnimatedBox animation={fadeIn} threshold={0.1}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      p: isMobile ? 3 : 4,
                      borderRadius: theme.shape.borderRadius,
                      boxShadow: theme.shadows[3],
                      background: theme.palette.background.paper,
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: theme.shadows[6],
                      },
                      transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: index === 0 ? theme.palette.primary.main : theme.palette.primary.light,
                        width: 56,
                        height: 56,
                        mb: 2.5,
                      }}
                    >
                      {React.cloneElement(feature.icon, { sx: { color: theme.palette.primary.contrastText, fontSize: '1.8rem' } })}
                    </Avatar>
                    <CardContent sx={{ flexGrow: 1, p: 0, width: '100%' }}>
                      <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: '600', fontSize: isMobile ? '1.1rem' : '1.2rem', mb: 1 }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65, px: 1 }}>
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </AnimatedBox>
              </Grid>
            ))}
          </Grid>
        </Container>

        <Divider sx={{ my: isMobile ? 6 : 10, mx: 'auto', width: '60%' }} />

        {/* How It Works Section */}
        <Container maxWidth={contentMaxWidth} sx={{ py: isMobile ? 6 : 10, mx: 'auto' }} ref={howItWorksRef}>
          <AnimatedBox animation={slideUp}>
            <Typography variant={isMobile ? 'h4' : 'h3'} component="h2" textAlign="center" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
              {t('howItWorksTitle')}
            </Typography>
            <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: isMobile ? 5 : 7, maxWidth: '800px', mx: 'auto', lineHeight: 1.7 }}>
              {t('howItWorksSubtitle')}
            </Typography>
          </AnimatedBox>
          <Grid container spacing={isMobile ? 3 : 3.5} justifyContent="center">
            {howItWorksSteps.map((step, index) => (
              <Grid item xs={12} md={6} key={index}>
                <AnimatedBox animation={index % 2 === 0 ? slideRight : slideLeft} threshold={0.1}>
                  <Paper
                    elevation={0}
                    variant="outlined"
                    sx={{
                      p: isMobile ? 2.5 : 3,
                      display: 'flex',
                      alignItems: 'center',
                      borderRadius: theme.shape.borderRadius,
                      borderColor: theme.palette.divider,
                      height: '100%',
                      flexDirection: 'row',
                      justifyContent: 'flex-start',
                      textAlign: 'left',
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        boxShadow: `0 0 15px ${theme.palette.primary.main}30`,
                      },
                      transition: 'border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: theme.palette.secondary.light,
                        color: theme.palette.secondary.contrastText,
                        mr: 2,
                        ml: 0.5,
                        flexShrink: 0,
                        width: 44,
                        height: 44,
                      }}
                    >
                      {React.cloneElement(step.icon, { sx: { fontSize: '1.6rem' } })}
                    </Avatar>
                    <Typography variant="body1" sx={{ flexGrow: 1, lineHeight: 1.6 }}>
                      {step.text}
                    </Typography>
                  </Paper>
                </AnimatedBox>
              </Grid>
            ))}
          </Grid>
        </Container>

        <Divider sx={{ my: isMobile ? 6 : 10, mx: 'auto', width: '60%' }} />

        {/* Why Choose Us Section */}
        <Container maxWidth={contentMaxWidth} sx={{ py: isMobile ? 6 : 10, mx: 'auto' }}>
          <AnimatedBox animation={slideUp}>
            <Typography variant={isMobile ? 'h4' : 'h3'} component="h2" textAlign="center" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
              {t('whyChooseUsTitle')}
            </Typography>
            <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: isMobile ? 5 : 7, maxWidth: '800px', mx: 'auto', lineHeight: 1.7 }}>
              {t('whyChooseUsSubtitle')}
            </Typography>
          </AnimatedBox>
          <Grid container spacing={isMobile ? 3 : 3.5} justifyContent="center">
            {whyChooseUsData.map((item, index) => (
              <Grid item xs={12} sm={6} md={6} key={index}>
                <AnimatedBox animation={fadeIn} threshold={0.1}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      p: isMobile ? 3 : 4,
                      borderRadius: theme.shape.borderRadius,
                      boxShadow: theme.shadows[3],
                      background: theme.palette.background.paper,
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: theme.shadows[6],
                      },
                      transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                    }}
                  >
                    <Avatar sx={{ bgcolor: theme.palette.primary.light, width: 56, height: 56, mb: 2.5 }}>
                      {React.cloneElement(item.icon, { sx: { color: theme.palette.primary.contrastText, fontSize: '1.8rem' } })}
                    </Avatar>
                    <CardContent sx={{ flexGrow: 1, p: 0, width: '100%' }}>
                      <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: '600', fontSize: isMobile ? '1.1rem' : '1.2rem', mb: 1 }}>
                        {item.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65, px: 1 }}>
                        {item.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </AnimatedBox>
              </Grid>
            ))}
          </Grid>
        </Container>

        <Divider sx={{ my: isMobile ? 6 : 10, mx: 'auto', width: '60%' }} />

        {/* Statistics Section - Example */}
        <Container maxWidth={contentMaxWidth} sx={{ py: isMobile ? 5 : 8, textAlign: 'center', mx: 'auto' }}>
          <AnimatedBox animation={fadeIn}>
            <Typography variant={isMobile ? 'h4' : 'h3'} component="h2" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
              {t('statsTitle')}
            </Typography>
            <Grid container spacing={isMobile ? 2 : 3} justifyContent="center" alignItems="center">
              <Grid item xs={12} sm={5}>
                <Paper
                  elevation={0}
                  variant="outlined"
                  sx={{
                    p: isMobile ? 2.5 : 3.5,
                    borderRadius: theme.shape.borderRadius,
                    borderColor: theme.palette.divider,
                    height: '100%',
                  }}
                >
                  <Typography variant={isMobile ? 'h3' : 'h2'} sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                    {totalConversions.toLocaleString()}
                  </Typography>
                  <Typography variant="h6" color="text.secondary">
                    {t('statsConverted')}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={5}>
                <Paper
                  elevation={0}
                  variant="outlined"
                  sx={{
                    p: isMobile ? 2.5 : 3.5,
                    borderRadius: theme.shape.borderRadius,
                    borderColor: theme.palette.divider,
                    height: '100%',
                  }}
                >
                  <Typography variant={isMobile ? 'h3' : 'h2'} sx={{ fontWeight: 'bold', color: theme.palette.secondary.main }}>
                    {Object.keys(i18n.options.resources).length}
                  </Typography>
                  <Typography variant="h6" color="text.secondary">
                    {t('statsLanguages')}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </AnimatedBox>
        </Container>

        {/* Footer */}
        <Box
          component="footer"
          sx={{
            py: 4,
            px: 2,
            mt: 'auto',
            backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[100],
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Container maxWidth="lg">
            <Grid container spacing={3} justifyContent="space-between" alignItems="center">
              <Grid item xs={12} md={4} sx={{ textAlign: isMobile ? 'center' : 'left' }}>
                <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                  {t('siteName')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  © {new Date().getFullYear()} {t('footerRights')}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
                <AppLanguageSwitcher inAppBar={false} />
              </Grid>
              <Grid item xs={12} md={4} sx={{ textAlign: isMobile ? 'center' : 'right' }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('footerContact')}
                </Typography>
                <Link
                  href={`mailto:${t('contactEmail')}`}
                  color="text.primary"
                  sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline', color: theme.palette.primary.main } }}
                >
                  {t('contactEmail')}
                </Link>
              </Grid>
            </Grid>
          </Container>
        </Box>
      </Box>

      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Floating Action Button for Scroll to Top - Example */}
      {!appBarSolid && scrollYProgress > 0.2 && (
        <Zoom in={true}>
          <IconButton
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            sx={{
              position: 'fixed',
              bottom: 16,
              right: 16,
              backgroundColor: theme.palette.primary.main,
              color: 'white',
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              },
            }}
          >
            <ArrowUpwardIcon />
          </IconButton>
        </Zoom>
      )}
    </Suspense>
  );
}

export default App;
